import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Wind, Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    let result;
    if (isLogin) {
      result = await login(email, password);
    } else {
      if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
      result = await register(name, email, password);
    }
    
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--page-bg)' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #10B981 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}></div>
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="flex items-center gap-3 mb-8">
            <Wind className="w-10 h-10 text-white" strokeWidth={1.5} />
            <span className="text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Vayu Sense</span>
          </div>
          <h2 className="text-4xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            Monitor. Protect.<br />Breathe Better.
          </h2>
          <p className="text-white/80 text-lg leading-relaxed max-w-md">
            Access your Vayu Sense dashboard to monitor real-time air quality, manage your smart windows, and keep your indoor air clean.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm mb-8 hover:opacity-70 transition-opacity" style={{ color: 'var(--text-muted)', fontFamily: 'Manrope' }} data-testid="back-to-home-link">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Wind className="w-7 h-7" style={{ color: 'var(--brand-sky)' }} strokeWidth={1.5} />
            <span className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: 'var(--text-primary)' }}>Vayu Sense</span>
          </div>

          <h1 className="text-3xl font-medium mb-2" style={{ fontFamily: 'Outfit', color: 'var(--text-primary)' }} data-testid="auth-title">
            {isLogin ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="mb-8" style={{ color: 'var(--text-secondary)', fontFamily: 'Manrope' }}>
            {isLogin ? 'Sign in to access your dashboard' : 'Start monitoring your air quality'}
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-xl text-sm" style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)', fontFamily: 'Manrope' }}>Full Name</label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-xl text-sm"
                  style={{ border: '1px solid var(--border-color)', fontFamily: 'Manrope', background: 'white' }}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)', fontFamily: 'Manrope' }}>Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={{ border: '1px solid var(--border-color)', fontFamily: 'Manrope', background: 'white' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)', fontFamily: 'Manrope' }}>Password</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  className="w-full px-4 py-3 rounded-xl text-sm pr-12"
                  style={{ border: '1px solid var(--border-color)', fontFamily: 'Manrope', background: 'white' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                  style={{ color: 'var(--text-muted)' }}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3.5 text-base"
              data-testid="auth-submit-btn"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <p className="text-center mt-8 text-sm" style={{ color: 'var(--text-muted)', fontFamily: 'Manrope' }}>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="font-semibold hover:underline"
              style={{ color: 'var(--brand-sky)' }}
              data-testid="auth-toggle-btn"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
